import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Modal, Drawer, message, Button } from "antd";
import MShippingAddress from "Components/mobile/order/MShippingAddress";
import MOrderSumCard from "Components/mobile/order/MOrderSumCard";
import MOrderInfoCard from "Components/mobile/order/MOrderInfoCard";
import MOrderProductCard from "Components/mobile/order/MOrderProductCard";
import MConfirmChannelCard from "Components/mobile/order/MConfirmChannelCard";
import SwitchShipToPage from "Components/common/SwitchShipToPage";
import { AppContext } from "Store";
import { isValidEmail, getAuthToken, formatPrice } from "Utils/common";
import {
  getSelectedSTObj,
  getSelectedBTObj,
  getContactName,
  getContactPhone,
  getContactPhoneForChannel,
  isValidChinesePhoneWithCountryCode,
} from "Utils/addressUtils";
import MHeader from "Components/mobile/common/MHeader";
import axios from "axios";
import {
  enableOrderPreviewCRD,
  orderConfirmUrl,
  productCRDRangeUrl,
  submitOrderUrl,
  USER_TYPE,
  ORDER_CHANNEL,
  NOTIFICATION_METHOD,
  openTfsRuleUrl,
  openTfsChangeRuleUrl,
  AMOUNT_EXCEEDS_LIMIT,
  ACTIVITY_NO_PERMISSION_STATUS_VALUE,
  getActivityNoPermissionStatusLabel,
} from "Utils/constant";
import Loading from "Components/common/Loading";
import MFooter from "Components/mobile/common/MFooter";
import { pageDetailsDataPush } from "Utils/analyticsUtils";
import { submitOrderErrorHandle } from "Utils/activityHelper";
import { decrypt } from "Utils/crypto";
import { submitOrderEvent } from "Utils/aaUtils";
import OrderPreviewCRDModal from "Components/order/OrderPreviewCRDModal";
import {
  applyCRDSelection,
  buildCRDRangeRequest,
  mergeCRDRangeData,
  normalizeOrderLinesForSubmit,
} from "Utils/orderPreviewCRD";

const formatPhone = (phone: string) => {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("86") && digits.length > 11) {
    return digits.slice(-11);
  }
  if (digits.length > 11) return digits.slice(-11);
  return digits;
};

const MOrderPreview: React.FC = () => {
  const { loginState, refreshCartData, cartData, activityPermissionMeta } =
    useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { activityId } = useParams<{ activityId: string }>();
  const activityNoPermissionLabel = getActivityNoPermissionStatusLabel(
    activityPermissionMeta.activityId === activityId
      ? activityPermissionMeta.customerDataType
      : undefined
  );
  const [previewReqbody, setPreviewReqbody] = useState<any>();
  const [previewData, setPreviewData] = useState<any>();
  const [stContact, setStContact] = useState<string>("");
  const [stContactPhone, setStContactPhone] = useState<string>("");
  const [errMsg, setErrMsg] = useState<Object>({});
  const [billContact, setBillContact] = useState<string>("");
  const [billContactPhone, setBillContactPhone] = useState<string>("");
  const [inOrderNumber, setInOrderNumber] = useState<string>("");
  const [remark, setRemark] = useState<string>("");
  const [emails, setEmails] = useState<string[]>([]);
  const [confirmChannels, setConfirmChannels] = useState<{
    app: boolean;
    sms: boolean;
    mail: boolean;
  }>({
    app: true,
    sms: false,
    mail: false,
  });
  const [confirmSms, setConfirmSms] = useState<string>("");
  const [confirmEmail, setConfirmEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [STAddressSelector, setSTAddressSelector] = useState<boolean>(false);
  const [resetContactInfo, setResetContactInfo] = useState<number>(1);
  const [showAmountExceedsLimitModal, setShowAmountExceedsLimitModal] = useState(false);
  const [crdHolidays, setCRDHolidays] = useState<string[]>([]);
  const [openCRDModal, setOpenCRDModal] = useState<boolean>(false);
  const [currentCRDProduct, setCurrentCRDProduct] = useState<any>(null);
  const crdRequestKeyRef = useRef("");

  const selectedST = getSelectedSTObj(loginState);
  const selectedBT = getSelectedBTObj(loginState);
  const canShowCRD = enableOrderPreviewCRD && !!previewReqbody?.shipTo;

  const needConfirmChannelModule =
    loginState.userType === USER_TYPE.EMPLOYEE &&
    loginState?.proxyUser?.orderChannel === ORDER_CHANNEL.ONLINE;

  const updatePreviewData = (respData: any) => {
    setPreviewData({
      ...respData,
      products: normalizeOrderLinesForSubmit(respData?.products || []),
    });
    crdRequestKeyRef.current = "";
  };

  const smsCandidates = useMemo(() => {
    if (!needConfirmChannelModule) return [];
    const phoneFromAddress = getContactPhoneForChannel(selectedST, loginState.user);
    const phoneFromUser = loginState?.user?.user?.contactInfo?.phone;
    const phoneFromContactInput = (() => {
      if (!stContactPhone && !stContact) return "";
      const phone = stContactPhone || "";
      if (phone) return phone.replace(/[()\uFF08\uFF09]/g, "");
      const cleaned = stContact.replace(/[()\uFF08\uFF09]/g, "");
      const parts = cleaned.split(/[,，\s+]/).filter(Boolean);
      return parts.length ? parts[parts.length - 1] : "";
    })();
    let receiverTelephone = phoneFromAddress;
    const normalizedContactInput = phoneFromContactInput
      ? phoneFromContactInput.replace(/[()\uFF08\uFF09]/g, "").trim()
      : "";

    const contactInputValid =
      normalizedContactInput &&
      isValidChinesePhoneWithCountryCode(normalizedContactInput);
    if (contactInputValid && normalizedContactInput !== phoneFromAddress) {
      receiverTelephone = normalizedContactInput;
    }
    const list = contactInputValid
      ? [receiverTelephone, phoneFromUser, normalizedContactInput]
      : [receiverTelephone, phoneFromUser];
    return Array.from(
      new Set(
        list
          .filter(Boolean)
          .map((p) => formatPhone(p))
          .filter(Boolean)
      )
    );
  }, [needConfirmChannelModule, selectedST, loginState, stContact, stContactPhone]);

  useEffect(() => {
    const temp = localStorage.getItem(`${activityId}OrderPreviewData`);
    const encryptedData = temp && JSON.parse(temp);
    if (!encryptedData) {
      navigate(-1);
      return;
    }
    decrypt(encryptedData)
      .then((decryptedStr) => {
        const data = JSON.parse(decryptedStr);
        updatePreviewData(data.respData);
        setPreviewReqbody(data.reqBody);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
    const aaPageDetail = {
      type: "campaigns",
      name: "campaigns:orderConfirmation",
    };
    pageDetailsDataPush(aaPageDetail);
    (window as any).currentCampaignId = activityId;
    return () => {
      localStorage.removeItem(`${activityId}OrderPreviewData`);
    };
  }, [activityId, navigate]);

  useEffect(() => {
    const shipToNum = previewReqbody?.shipTo;
    const authToken = getAuthToken();
    if (
      !enableOrderPreviewCRD ||
      !authToken ||
      !previewData?.products?.length ||
      !shipToNum
    ) {
      return;
    }
    const reqBody = buildCRDRangeRequest({
      shipToNum,
      supplyCenterId: previewData.supplyCenterId,
      products: previewData.products,
    });
    if (!reqBody.items.length) {
      return;
    }
    const requestKey = JSON.stringify({
      shipToNum: reqBody.shipToNum,
      supplyCenterId: reqBody.supplyCenterId,
      items: reqBody.items,
    });
    if (crdRequestKeyRef.current === requestKey) {
      return;
    }
    crdRequestKeyRef.current = requestKey;
    axios(productCRDRangeUrl, {
      method: "POST",
      headers: {
        Authorization: authToken,
        "Content-Type": "application/json",
      },
      data: reqBody,
    })
      .then((resp) => {
        if (crdRequestKeyRef.current !== requestKey) {
          return;
        }
        const respData = resp.data;
        if (respData?.status !== "success") {
          crdRequestKeyRef.current = "";
          setCRDHolidays([]);
          return;
        }
        setCRDHolidays(respData.data?.holidays || []);
        setPreviewData((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            products: mergeCRDRangeData(prev.products, respData.data),
          };
        });
      })
      .catch(() => {
        if (crdRequestKeyRef.current !== requestKey) {
          return;
        }
        crdRequestKeyRef.current = "";
        setCRDHolidays([]);
      });
  }, [previewData?.products, previewData?.supplyCenterId, previewReqbody?.shipTo]);

  useEffect(() => {
    if (loginState.status !== "login") return;
    const currentSelectedST = getSelectedSTObj(loginState);
    const currentSelectedBT = getSelectedBTObj(loginState);
    setStContact(getContactName(currentSelectedST, loginState.user));
    setBillContact(getContactName(currentSelectedBT, loginState.user));
    setStContactPhone(getContactPhone(currentSelectedST, loginState.user));
    setBillContactPhone(getContactPhone(currentSelectedBT, loginState.user));
  }, [loginState, resetContactInfo]);

  useEffect(() => {
    if (loginState?.status !== "login") return;
    const phone = loginState?.user?.user?.contactInfo?.phone || "";
    const email = loginState?.user?.user?.contactInfo?.primaryEmail || "";
    setConfirmSms(formatPhone(phone));
    setConfirmEmail(email);
  }, [loginState]);

  type ConfirmChannelKey = "app" | "sms" | "mail";
  const handleChannelChange = (key: ConfirmChannelKey) => {
    setConfirmChannels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const gotoOrderConfirm = (stItem: any, preSelectedAddressKey?: any) => {
    const currentSelectedST = getSelectedSTObj(loginState);
    const currentSelectedBT = getSelectedBTObj(loginState);
    setStContact(getContactName(currentSelectedST, loginState.user));
    setBillContact(getContactName(currentSelectedBT, loginState.user));
    setStContactPhone(getContactPhone(currentSelectedST, loginState.user));
    setBillContactPhone(getContactPhone(currentSelectedBT, loginState.user));
    setLoading(true);
    const nextPreviewReqbody = { ...previewReqbody, shipTo: stItem.shipTo };
    axios(orderConfirmUrl, {
      method: "POST",
      headers: {
        Authorization: getAuthToken(),
        "Content-Type": "application/json",
      },
      data: nextPreviewReqbody,
    })
      .then((resp) => {
        if (resp.status === 200) {
          const respData = resp.data;
          if (respData?.status === "success") {
            respData.data.activityId = previewData.activityId;
            setLoading(false);
            if (!respData.data?.matchStatus) {
              Modal.confirm({
                className: "confirmModal",
                title: "请确认地址更改？",
                content: "地址更改后，活动优惠重新计算，请返回上一个页面确认优惠明细",
                okText: "返回上一页",
                cancelText: "不更改地址",
                onOk() {
                  navigate(-1);
                },
                onCancel() {
                  localStorage.setItem(
                    "selectedAddressKey",
                    preSelectedAddressKey || ""
                  );
                  setPreviewData((prev: any) => ({ ...prev }));
                  setResetContactInfo((prev) => prev + 1);
                },
              });
              return;
            }
            setPreviewReqbody(nextPreviewReqbody);
            updatePreviewData(respData.data);
          } else if (respData?.status === ACTIVITY_NO_PERMISSION_STATUS_VALUE) {
            message.warning(activityNoPermissionLabel);
            setLoading(false);
          } else {
            message.error("操作失败，请联系在线客服");
            setLoading(false);
          }
        } else {
          message.error("操作失败，请联系在线客服");
          setLoading(false);
        }
      })
      .catch(() => {
        message.error("操作失败，请联系在线客服");
        setLoading(false);
      });
  };

  const handleCRDSubmit = (payload: {
    newCrd: string;
    selectedLinesMap: Record<string, string>;
  }) => {
    setPreviewData((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        products: applyCRDSelection(prev.products, payload),
      };
    });
    setOpenCRDModal(false);
    setCurrentCRDProduct(null);
  };

  const submitOrder = () => {
    if (loading) return;
    setLoading(true);
    const temp2: any = localStorage.getItem(`${activityId}OrderPreviewData`);
    if (!temp2) {
      message.error(
        "订单数据丢失，请重新选择商品， 或者请勿打开多个订单提交页面"
      );
      setLoading(false);
      return;
    }
    if (previewData.total === "0" || previewData.total === "0.0" || previewData.total === "0.00") {
      setLoading(false);
      return message.warning("当前订单金额为0, 无法提交订单");
    }
    const nextErrMsg: any = {};
    if (!stContact) {
      nextErrMsg.stContact = "联系人姓名不能为空";
    }
    if (!billContact) {
      nextErrMsg.billContact = "联系人姓名不能为空";
    }
    if (!stContactPhone) {
      nextErrMsg.stContactPhone = "联系人电话不能为空";
    }
    if (!billContactPhone) {
      nextErrMsg.billContactPhone = "联系人电话不能为空";
    }
    if (Object.keys(nextErrMsg).length > 0) {
      setErrMsg(nextErrMsg);
      setLoading(false);
      return;
    }
    const emailList = emails.filter((email) => email.trim());
    const valid3 = emailList.length === 0 || emailList.every((email) => isValidEmail(email));
    if (!valid3) {
      setLoading(false);
      return;
    }

    const parseAmount = (value?: string | number) => {
      if (!value) return 0;
      if (typeof value === "number") return value;
      return parseFloat(String(value).replace(/,/g, "")) || 0;
    };

    const isEmployeeOffline =
      loginState.userType === USER_TYPE.EMPLOYEE &&
      loginState?.proxyUser?.orderChannel === ORDER_CHANNEL.OFFLINE;
    const isScEmployeeModule = loginState.userType === USER_TYPE.EMPLOYEE;
    const payableAmount = parseAmount(previewData.total);
    if (isEmployeeOffline && payableAmount > AMOUNT_EXCEEDS_LIMIT) {
      setShowAmountExceedsLimitModal(true);
      setLoading(false);
      return;
    }
    if (needConfirmChannelModule) {
      const normalizedSms = confirmSms.replace(/[()\uFF08\uFF09]/g, "");
      const formattedSms = formatPhone(normalizedSms);
      const formattedCandidates = smsCandidates
        .map((p) => formatPhone((p || "").replace(/[()\uFF08\uFF09]/g, "")))
        .filter(Boolean);
      if (confirmChannels.sms && !confirmSms) {
        message.warning("请填写短信手机号或取消短信渠道选择");
        setLoading(false);
        return;
      }
      if (confirmChannels.mail && !confirmEmail) {
        message.warning("请填写邮件地址或取消邮件渠道选择");
        setLoading(false);
        return;
      }
      if (
        confirmChannels.sms &&
        formattedSms &&
        !formattedCandidates.includes(formattedSms)
      ) {
        message.warning(
          "提醒：短信通知电话必须是收件人联系方式或者账户默认联系方式，请检查并更新您选择的联系方式"
        );
        setLoading(false);
        return;
      }
    }

    interface NotificationMethod {
      method: typeof NOTIFICATION_METHOD[keyof typeof NOTIFICATION_METHOD];
      receiver: string;
    }

    interface SubmitOrderBody {
      tieredRuleId: any;
      matchStatus: any;
      shipTo: any;
      prePaymentAccount: any;
      customerPrimaryEmail: any;
      paymentNumber: any;
      additionalEmail: string[];
      siebelRowId: any;
      supplyCenterId: any;
      remark: any;
      orderLines: any;
      addresses: any[];
      userKey?: number;
      userName?: string;
      priceDisplayFlag?: string;
      orderChannel?: typeof ORDER_CHANNEL[keyof typeof ORDER_CHANNEL];
      confirmChannels?: { app: boolean; sms: boolean; mail: boolean };
      confirmSms?: string;
      confirmEmail?: string;
      notificationMethods?: NotificationMethod[];
    }

    const reqBody: SubmitOrderBody = {
      tieredRuleId: previewReqbody.tieredRuleId,
      matchStatus: previewData.matchStatus,
      shipTo: selectedST?.accountNumber,
      prePaymentAccount: selectedBT.prePaymentAccount,
      customerPrimaryEmail: loginState?.user?.user.contactInfo.primaryEmail,
      paymentNumber: inOrderNumber,
      additionalEmail: emailList,
      siebelRowId: loginState?.user?.user?.siebelRowId,
      supplyCenterId: previewData.supplyCenterId,
      remark: remark,
      orderLines: normalizeOrderLinesForSubmit(previewData.products),
      addresses: [
        {
          addressKey: selectedST.addressKey,
          attentionLine: `${stContact},${stContactPhone}`,
          accountId: selectedST?.accountNumber,
          accountName: selectedST.companyOrInstitution,
          address1: selectedST.deptOrLab || null,
          address2: selectedST.buildingOrRoom || null,
          address3: selectedST.streetAddress || null,
          countryCode: selectedST.isoCountryCode,
          country: selectedST.country,
          city: selectedST.city,
          addressType: "SHIP_TO",
          billToId: selectedST.relatedBillTo,
        },
        {
          attentionLine: `${billContact},${billContactPhone}`,
          address1: selectedBT.deptOrLab || null,
          address2: selectedBT.buildingOrRoom || null,
          address3: selectedBT.streetAddress || null,
          accountName: selectedBT.companyOrInstitution,
          countryCode: selectedBT.isoCountryCode,
          city: selectedBT.city,
          addressType: "BILL_TO",
          addressKey: selectedBT.addressKey,
          accountId: selectedBT.accountNumber,
        },
      ],
      userKey: isScEmployeeModule ? undefined : loginState?.user?.user?.userId,
      userName: loginState?.user?.user?.userName,
      priceDisplayFlag: selectedST?.priceDisplayFlag,
    };
    if (loginState.userType === USER_TYPE.EMPLOYEE) {
      const channel = loginState?.proxyUser?.orderChannel;
      if (channel === ORDER_CHANNEL.ONLINE) {
        reqBody.orderChannel = ORDER_CHANNEL.ONLINE;
        const notificationMethods: NotificationMethod[] = [];
        if (confirmChannels.mail && confirmEmail) {
          notificationMethods.push({ method: NOTIFICATION_METHOD.MAIL, receiver: confirmEmail });
        }
        if (confirmChannels.sms && confirmSms) {
          notificationMethods.push({ method: NOTIFICATION_METHOD.SMS, receiver: confirmSms });
        }
        if (notificationMethods.length) {
          reqBody.notificationMethods = notificationMethods;
        }
      } else if (channel === ORDER_CHANNEL.OFFLINE) {
        reqBody.orderChannel = ORDER_CHANNEL.OFFLINE;
      }
    }
    axios(submitOrderUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthToken(),
      },
      data: reqBody,
    })
      .then((resp) => {
        const respData = resp.data;
        if (respData.status === "success") {
          refreshCartData();
          const searchParams = new URLSearchParams(location.search);
          submitOrderEvent(activityId, previewData);
          navigate(`/success?${searchParams.toString()}`, {
            state: {
              activityId: activityId,
              data: respData.data,
              hasShipTo: selectedST?.accountNumber,
              payableTotal: payableAmount,
            },
          });
          return;
        }
        if (respData?.status === ACTIVITY_NO_PERMISSION_STATUS_VALUE) {
          message.warning(activityNoPermissionLabel);
          setLoading(false);
          return;
        }
        submitOrderErrorHandle(respData, message);
        setLoading(false);
      })
      .catch(() => {
        message.error("提交订单失败, 请重试！");
        setLoading(false);
      });
  };

  if (!previewData) {
    if (loading) return <Loading />;
    navigate(-1);
    return (
      <>
        <div>请去到详情页面</div>
      </>
    );
  }
  return (
    <>
      <MHeader title="组合订单提交" />
      <div className="mobilePageBody mOrderPreviewPage">
        <div className="cardWrapper">
          <MShippingAddress
            selectedST={selectedST}
            stContact={stContact}
            setStContact={setStContact}
            stContactPhone={stContactPhone}
            setStContactPhone={setStContactPhone}
            setShowSTSelector={() => {}}
            errMsg={errMsg}
            setErrMsg={setErrMsg}
            selectedBT={selectedBT}
            billContact={billContact}
            setBillContact={setBillContact}
            billContactPhone={billContactPhone}
            setBillContactPhone={setBillContactPhone}
            primaryEmail={loginState?.user?.user?.contactInfo.primaryEmail}
            emails={emails}
            setEmails={setEmails}
            onAddressChangeClick={() => {
              setSTAddressSelector(true);
            }}
          />
        </div>

        <div className="cardWrapper">
          <MOrderInfoCard
            inOrderNumber={inOrderNumber}
            setInOrderNumber={setInOrderNumber}
            remark={remark}
            setRemark={setRemark}
          />
        </div>

        {needConfirmChannelModule && (
          <div className="confirmChannelCardWrapper">
            <MConfirmChannelCard
              confirmChannels={confirmChannels}
              confirmSms={confirmSms}
              confirmEmail={confirmEmail}
              smsCandidates={smsCandidates}
              onChannelChange={handleChannelChange}
              onSmsChange={setConfirmSms}
              onEmailChange={setConfirmEmail}
            />
          </div>
        )}

        <div className="productsWrapper">
          {previewData.products.map((product: any) => {
            return (
              <MOrderProductCard
                orderProduct={product}
                key={product.lineNumber}
                cartData={cartData}
                previewData={previewData}
                isOrderPreviewFlag={true}
                enableCRD={canShowCRD}
                onEditCRD={(currentProduct: any) => {
                  setCurrentCRDProduct(currentProduct);
                  setOpenCRDModal(true);
                }}
              />
            );
          })}
        </div>
        <div className="orderSumWrapper">
          <MOrderSumCard priceData={previewData} />
        </div>
      </div>
      <div className="submitFooter">
        <div className="left">
          <span className="total">{formatPrice(previewData.total)}</span>
          <span className="count">{previewData?.products?.length}项</span>
        </div>
        <div className="right">
          <button className="btn-confirm" onClick={submitOrder}>
            提交
          </button>
        </div>
      </div>
      <OrderPreviewCRDModal
        open={openCRDModal}
        currentProduct={currentCRDProduct}
        products={previewData.products}
        holidays={crdHolidays}
        onClose={() => {
          setOpenCRDModal(false);
          setCurrentCRDProduct(null);
        }}
        onSubmit={handleCRDSubmit}
      />
      {loading && <Loading />}
      <Modal
        title="订单无法提交"
        open={showAmountExceedsLimitModal}
        getContainer={document.body}
        onCancel={() => setShowAmountExceedsLimitModal(false)}
        className="show-go-to-detail-info-modal amount-exceeds-limit-modal"
        footer={[
          <Button key="cancel" type="primary" className="goback-btn" onClick={() => setShowAmountExceedsLimitModal(false)}>
            返回
          </Button>,
        ]}
      >
        <div>
          <p>
            根据赛默飞销售条款，单笔订单超6万金额，无法在本系统入单，您可返回购物车进行调整或准备好以下入单材料进行其他渠道入单
          </p>
          <p>
            1.请遵守《
            <label>
              <a
                href={openTfsRuleUrl}
                target="_blank"
                rel="noreferrer"
              >
                销售订单入单制度
              </a>
            </label>
            》，确保入单支持文件齐全，且须与客户签订双章销售合同。
          </p>
          <p>
            2.发货地址变更时，请按照公司《
            <label>
              <a
                href={openTfsChangeRuleUrl}
                target="_blank"
                rel="noreferrer"
              >
                交货地址变更政策
              </a>
            </label>
            》提供符合要求的地址确认文件。
          </p>
        </div>
      </Modal>
      <MFooter activityId={previewData.activityId} />
      <Drawer
        open={STAddressSelector}
        onClose={() => {
          setSTAddressSelector(false);
        }}
      >
        <SwitchShipToPage
          page="Order_Confirmation"
          showSwitchStPage={STAddressSelector}
          setShowSwitchStPage={setSTAddressSelector}
          onSTClick={gotoOrderConfirm}
        />
      </Drawer>
    </>
  );
};

export default MOrderPreview;
